import Avatar from 'antd/lib/avatar/avatar';
import { FORMAT_DATE_TIME } from 'constants/days';
import { LoggerActionEnum, LoggerActivityTypes } from 'constants/logger';
import useInfiniteDataQuery from 'hooks/useInfiniteDataQuery';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import moment from 'moment';
import React, { BaseSyntheticEvent } from 'react';
import { useParams } from 'react-router';
import { GET_LOGGER } from 'services/graphql/queries/logger.query';
import { GET_USERS_HAVE_TRANSACTION_ACTIVITY } from 'services/graphql/queries/user.query';
import { ITransactionLog } from 'types/transactionLog';
import { IUser } from 'types/user';
import { fastGetDefaultAvatarUrl } from 'utils/utils';

export const useTransactionActivityTabProps = (isActive?: boolean) => {
  const params: Record<string, string> = useParams();
  const transactionId = params.id;
  const userFilterOptionsMapper = (user: IUser) => ({
    value: user.id,
    label: (
      <div className='flex items-center'>
        <Avatar
          style={{ width: 25, height: 25 }}
          src={user.avatar?.thumbnailPipeFrom || fastGetDefaultAvatarUrl(user)}
        />
        <div className='ml-2'>{`${user.firstName} ${user.lastName}`}</div>
      </div>
    ),
  });
  const { fetchNextPageData, options: userFilterOptions } = useInfiniteDataQuery<IUser>({
    graphQLQuery: GET_USERS_HAVE_TRANSACTION_ACTIVITY,
    optionMapper: userFilterOptionsMapper,
    queryProps: {
      variables: {
        transactionId,
      },
    },
  });

  const onUserFilterScrollToGetNextData = (e: BaseSyntheticEvent) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
      fetchNextPageData?.();
    }
  };

  const loggerColumn = [
    {
      title: 'Date Time',
      dataIndex: 'createdAt',
      render: (createdAt: string) => <div>{moment(createdAt).format(FORMAT_DATE_TIME)}</div>,
    },
    {
      title: 'User',
      dataIndex: 'user',
      render: (user: IUser) =>
        user ? (
          <div className='flex items-center'>
            <div>
              <Avatar src={user?.avatar?.thumbnailPipeFrom || fastGetDefaultAvatarUrl(user)} />
            </div>
            <div className='ml-2'>
              {user.firstName} {user.lastName}
            </div>
          </div>
        ) : null,
    },
    {
      title: 'Activity Action',
      dataIndex: 'activityType',
      render: (activityType: LoggerActivityTypes) => <div className='uppercase'>{LoggerActionEnum[activityType]}</div>,
    },
    {
      title: 'Activity Message',
      dataIndex: 'details',
      render: (details: { message: string }) => <div>{details.message}</div>,
      width: '45%',
    },
  ];

  const dataMapper = (log: ITransactionLog) => ({ key: log.id, ...log });

  const tableProps = useReactiveQueryTable<ITransactionLog, ITransactionLog & { key: string }>({
    graphQLQuery: GET_LOGGER,
    queryProps: {
      variables: {
        transaction: transactionId,
      },
      fetchPolicy: 'cache-and-network',
      skip: !isActive,
    },
    columns: loggerColumn,
    dataMapper,
  });

  return { tableProps, userFilterOptions, onUserFilterScrollToGetNextData };
};
